import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CatalogService } from '@services/catalog.service';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { Vendor } from '@models/vendor';

export interface StockCatalogRow {
  _id: string;
  model: string;
  color: string;
  size: string;
  type?: string;
  vendorSku?: string;
  productId?: string;
}

@Component({
  selector: 'app-vendor-stock-order',
  templateUrl: './stock-order.component.html',
  styleUrls: ['./stock-order.component.css'],
})
export class VendorStockOrderComponent implements OnInit, OnChanges {
  @Input() vendor: Vendor;
  @Input() storeId: string | number;
  @ViewChild('poInput') poInput: ElementRef<HTMLInputElement>;

  orderDate = '';
  expectedDate = '';
  quantity: number | null = null;
  framesOrdered: number | null = null;
  plannedReturns: number | null = null;
  initials = '';
  search = '';
  catalog: StockCatalogRow[] = [];
  catalogLoading = false;
  checkedIds = new Set<string>();
  added: StockCatalogRow[] = [];
  purchaseOrderName = '';
  purchaseOrderFile: File | null = null;
  saving = false;

  constructor(
    private catalogService: CatalogService,
    private vendorOrderService: VendorOrderService,
    private auth: AuthService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.orderDate = this.isoDate(new Date());
    this.expectedDate = this.isoDate(this.addDays(new Date(), 28));
    this.initials = (this.auth.currentUser?.initials || '').toUpperCase();
    this.loadCatalog();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.vendor && !changes.vendor.firstChange) {
      this.clearBatch();
      this.loadCatalog();
    }
  }

  get visibleCatalog(): StockCatalogRow[] {
    const query = this.search.trim().toLowerCase();
    const rows = !query
      ? this.catalog
      : this.catalog.filter((row) =>
          `${row.model} ${row.color} ${row.size} ${row.vendorSku || ''}`
            .toLowerCase()
            .includes(query)
        );
    return rows.slice(0, 80);
  }

  get catalogMatchCount(): number {
    const query = this.search.trim().toLowerCase();
    if (!query) {
      return this.catalog.length;
    }
    return this.catalog.filter((row) =>
      `${row.model} ${row.color} ${row.size} ${row.vendorSku || ''}`
        .toLowerCase()
        .includes(query)
    ).length;
  }

  attachPurchaseOrder(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.poInput?.nativeElement.click();
  }

  onPurchaseOrderSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] || null;
    this.purchaseOrderFile = file;
    this.purchaseOrderName = file?.name || '';
  }

  onQuantity(value: string): void {
    const count = this.toCount(value);
    this.quantity = count;
    this.framesOrdered = count;
  }

  onFramesOrdered(value: string): void {
    const count = this.toCount(value);
    this.framesOrdered = count;
    this.quantity = count;
  }

  onPlannedReturns(value: string): void {
    this.plannedReturns = this.toCount(value);
  }

  trackRow(_index: number, row: StockCatalogRow): string {
    return row._id;
  }

  isChecked(row: StockCatalogRow): boolean {
    return this.checkedIds.has(row._id);
  }

  toggleRow(row: StockCatalogRow, checked: boolean): void {
    if (checked) {
      this.checkedIds.add(row._id);
    } else {
      this.checkedIds.delete(row._id);
    }
  }

  addSelected(): void {
    this.added = this.catalog.filter((row) => this.checkedIds.has(row._id));
    if (this.added.length) {
      this.quantity = this.added.length;
      this.framesOrdered = this.added.length;
    }
  }

  save(): void {
    const initials = (this.initials || '').trim().toUpperCase();
    const lines = this.added;
    const frames = this.framesOrdered || 0;
    if (!this.vendor?._id) {
      this.notificationService.show('error', 'Choose a vendor before saving a stock order.', 'Close');
      return;
    }
    if (!initials) {
      this.notificationService.show('error', 'Enter initials.', 'Close');
      return;
    }
    if (!this.orderDate) {
      this.notificationService.show('error', 'Enter an order date.', 'Close');
      return;
    }
    if (!lines.length && frames < 1) {
      this.notificationService.show('error', 'Enter frames ordered, or add catalog frames.', 'Close');
      return;
    }
    if (frames > 200 || lines.length > 200) {
      this.notificationService.show('error', 'A stock order can include up to 200 frames.', 'Close');
      return;
    }

    const body = {
      vendor: this.vendor._id,
      store: this.storeId != null && this.storeId !== '' ? this.storeId : this.auth.getStore(),
      user: this.auth.currentUser?._id,
      orderDate: this.orderDate,
      expectedDate: this.expectedDate || null,
      quantity: lines.length || frames,
      framesOrdered: lines.length || frames,
      plannedReturns: this.plannedReturns || 0,
      initials,
      lines: lines.map((row) => ({
        catalogProductId: row._id,
        productId: row.productId,
        model: row.model,
        color: row.color,
        size: row.size,
        type: row.type,
      })),
    };

    this.saving = true;
    const send = (attachment?: Record<string, unknown>) => {
      this.vendorOrderService
        .createStockOrder(attachment ? { ...body, purchaseOrderAttachment: attachment } : body)
        .subscribe({
          next: () => {
            const count = lines.length || frames;
            const returns = this.plannedReturns
              ? ` Planned returns: ${this.plannedReturns}.`
              : '';
            this.notificationService.show(
              'success',
              `Saved stock order. ${count} frame${count === 1 ? '' : 's'} added to Awaiting Frames.${returns}`,
              'Close'
            );
            this.clearBatch();
            this.saving = false;
          },
          error: (err) => {
            this.saving = false;
            this.notificationService.show(
              'error',
              err?.error?.message || 'Could not save the stock order.',
              'Close'
            );
          },
        });
    };

    if (this.purchaseOrderFile) {
      this.vendorOrderService.uploadPurchaseOrder(this.purchaseOrderFile).subscribe({
        next: (attachment) => send(attachment),
        error: () => {
          this.saving = false;
          this.notificationService.show('error', 'Could not attach the purchase order.', 'Close');
        },
      });
      return;
    }
    send();
  }

  private loadCatalog(): void {
    const vendorId = this.vendor?._id;
    this.catalog = [];
    if (!vendorId) {
      this.catalogLoading = false;
      return;
    }
    this.catalogLoading = true;
    this.catalogService.list(vendorId).subscribe((rows) => {
      if (this.vendor?._id !== vendorId) {
        return;
      }
      this.catalog = rows || [];
      this.catalogLoading = false;
    });
  }

  private clearBatch(): void {
    this.quantity = null;
    this.framesOrdered = null;
    this.plannedReturns = null;
    this.search = '';
    this.checkedIds.clear();
    this.added = [];
    this.purchaseOrderFile = null;
    this.purchaseOrderName = '';
    if (this.poInput?.nativeElement) {
      this.poInput.nativeElement.value = '';
    }
  }

  private toCount(value: string): number | null {
    if (value == null || String(value).trim() === '') {
      return null;
    }
    const count = Math.floor(Number(value));
    if (!Number.isFinite(count) || count < 0) {
      return null;
    }
    return count;
  }

  private isoDate(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }
}
